/**
 * Instagram/Snapchat-style pager gesture for V2 tab navigation.
 *
 * - Drives a MotionValue<number> representing the strip's x offset in pixels
 * - Uses native touch events (passive: false on touchmove) for direction lock
 * - Horizontal intent → preventDefault + drag the strip
 * - Vertical intent → do nothing, browser handles scroll natively
 * - Ignores touches starting inside [data-no-tab-swipe]
 * - Elastic resistance at edges
 * - Snaps with spring animation on release
 */
import { useEffect, useRef, type RefObject } from 'react';
import { type MotionValue } from 'framer-motion';
import { cssSnapTo, cssDragStart } from '@/constants/animation';

interface PagerGestureOpts {
  containerRef: RefObject<HTMLElement>;
  stripRef: RefObject<HTMLElement>;
  x: MotionValue<number>;
  activeIndex: number;
  pageCount: number;
  onPageChange: (index: number) => void;
  disabled?: boolean;
}

function isInsideNoSwipeZone(el: HTMLElement | null): boolean {
  let node: HTMLElement | null = el;
  while (node) {
    if (node.hasAttribute('data-no-tab-swipe')) return true;
    node = node.parentElement;
  }
  return false;
}

// CSS transition replaces framer-motion animate()

export function useV2SwipeGesture({
  containerRef,
  stripRef,
  x,
  activeIndex,
  pageCount,
  onPageChange,
  disabled = false,
}: PagerGestureOpts) {
  const stateRef = useRef({ activeIndex, pageCount, onPageChange, disabled });
  stateRef.current = { activeIndex, pageCount, onPageChange, disabled };

  useEffect(() => {
    let attachedEl: HTMLElement | null = null;

    const getPageWidth = () => attachedEl?.offsetWidth ?? containerRef.current?.offsetWidth ?? 0;

    let start: { px: number; py: number; startX: number; t: number } | null = null;
    let locked: 'h' | 'v' | null = null;

    const onTouchStart = (e: TouchEvent) => {
      if (stateRef.current.disabled) { start = null; return; }
      if (isInsideNoSwipeZone(e.target as HTMLElement)) { start = null; return; }
      cssDragStart(stripRef.current);
      const t = e.touches[0];
      start = { px: t.clientX, py: t.clientY, startX: x.get(), t: Date.now() };
      locked = null;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!start) return;
      const t = e.touches[0];
      const dx = t.clientX - start.px;
      const dy = t.clientY - start.py;

      if (!locked) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        locked = Math.abs(dx) > Math.abs(dy) * 1.2 ? 'h' : 'v';
      }

      if (locked === 'h') {
        e.preventDefault();
        const pageW = getPageWidth();
        const minX = -(pageCount - 1) * pageW;
        let newX = start.startX + dx;
        if (newX > 0) newX *= 0.25;
        else if (newX < minX) newX = minX + (newX - minX) * 0.25;
        x.set(newX);
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (!start || locked !== 'h') { start = null; locked = null; return; }

      const { activeIndex: idx, pageCount: count, onPageChange: onChange } = stateRef.current;
      const t = e.changedTouches[0];
      const dx = t.clientX - start.px;
      const dt = Math.max(Date.now() - start.t, 1);
      const vx = (dx / dt) * 1000;
      const pageW = getPageWidth();

      let target = idx;
      if (Math.abs(dx) > pageW * 0.15 || Math.abs(vx) > 300) {
        target = dx < 0 ? idx + 1 : idx - 1;
      }
      target = Math.max(0, Math.min(count - 1, target));

      cssSnapTo(x, -target * pageW, stripRef.current);
      if (target !== idx) onChange(target);

      start = null;
      locked = null;
    };

    const onTouchCancel = () => {
      if (start && locked === 'h') {
        const pageW = getPageWidth();
        cssSnapTo(x, -stateRef.current.activeIndex * pageW, stripRef.current);
      }
      start = null;
      locked = null;
    };

    const bind = (el: HTMLElement) => {
      el.addEventListener('touchstart', onTouchStart, { passive: true });
      el.addEventListener('touchmove', onTouchMove, { passive: false });
      el.addEventListener('touchend', onTouchEnd, { passive: true });
      el.addEventListener('touchcancel', onTouchCancel, { passive: true });
    };

    const unbind = (el: HTMLElement) => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchCancel);
    };

    const syncToActivePage = () => {
      const pageW = getPageWidth();
      if (pageW > 0) {
        x.set(-stateRef.current.activeIndex * pageW);
      }
    };

    const refreshBindings = () => {
      const nextEl = containerRef.current;
      if (attachedEl === nextEl) {
        syncToActivePage();
        return;
      }

      if (attachedEl) unbind(attachedEl);
      attachedEl = nextEl;
      if (attachedEl) bind(attachedEl);
      syncToActivePage();
    };

    refreshBindings();
    window.addEventListener('pageshow', refreshBindings);
    window.addEventListener('focus', refreshBindings);
    document.addEventListener('visibilitychange', refreshBindings);

    return () => {
      if (attachedEl) unbind(attachedEl);
      window.removeEventListener('pageshow', refreshBindings);
      window.removeEventListener('focus', refreshBindings);
      document.removeEventListener('visibilitychange', refreshBindings);
    };
  }, [containerRef, x, activeIndex, pageCount, onPageChange]);
}
